import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Zap, Search, X, Trash2, Package, Loader2, Save, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUICK_KEY_SLOTS = 12;

// Color palette for quick keys
const QUICK_KEY_COLORS = [
  'bg-orange-500', 'bg-blue-500', 'bg-emerald-500', 'bg-purple-500',
  'bg-pink-500', 'bg-cyan-500', 'bg-amber-500', 'bg-indigo-500',
  'bg-rose-500', 'bg-teal-500', 'bg-lime-500', 'bg-violet-500'
];

const QuickKeysSettings = () => {
  const { getAuthHeader } = useAuth();
  const [products, setProducts] = useState([]);
  const [quickKeys, setQuickKeys] = useState(Array(QUICK_KEY_SLOTS).fill(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/products?limit=10000`, { headers: getAuthHeader() });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  }, [getAuthHeader]);

  // Fetch quick keys
  const fetchQuickKeys = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/quick-keys`, { headers: getAuthHeader() });
      const keys = response.data?.keys || [];
      // Pad to QUICK_KEY_SLOTS
      const paddedKeys = [...keys];
      while (paddedKeys.length < QUICK_KEY_SLOTS) {
        paddedKeys.push(null);
      }
      setQuickKeys(paddedKeys.slice(0, QUICK_KEY_SLOTS));
    } catch (error) {
      setQuickKeys(Array(QUICK_KEY_SLOTS).fill(null));
    }
  }, [getAuthHeader]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchProducts(), fetchQuickKeys()]);
      setLoading(false);
    };
    init();
  }, [fetchProducts, fetchQuickKeys]);

  // Save quick keys
  const saveQuickKeys = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings/quick-keys`, { keys: quickKeys }, { headers: getAuthHeader() });
      toast.success('Quick Keys saved successfully!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save Quick Keys');
    } finally {
      setSaving(false);
    }
  };

  // Assign product to slot
  const assignProduct = (product) => {
    if (editingSlot === null) return;
    
    const newKeys = [...quickKeys];
    newKeys[editingSlot] = {
      product_id: product.id,
      product_name: product.name,
      sell_price: product.sell_price,
      color: QUICK_KEY_COLORS[editingSlot % QUICK_KEY_COLORS.length]
    };
    setQuickKeys(newKeys);
    setHasChanges(true);
    setEditingSlot(null);
    setSearchQuery('');
  };

  // Remove product from slot
  const removeProduct = (index) => {
    const newKeys = [...quickKeys];
    newKeys[index] = null;
    setQuickKeys(newKeys);
    setHasChanges(true);
  };

  // Clear all quick keys
  const clearAll = () => {
    if (window.confirm('Are you sure you want to clear all Quick Keys?')) {
      setQuickKeys(Array(QUICK_KEY_SLOTS).fill(null));
      setHasChanges(true);
    }
  };

  // Filter products for search
  const filteredProducts = searchQuery.length >= 2
    ? products.filter(p => {
        const term = searchQuery.toLowerCase();
        return p.name.toLowerCase().includes(term) ||
          p.sku?.toLowerCase().includes(term) ||
          p.barcode?.includes(searchQuery) ||
          p.brand?.toLowerCase().includes(term) ||
          p.category?.toLowerCase().includes(term) ||
          p.supplier_name?.toLowerCase().includes(term);
      }).slice(0, 10)
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quick-keys-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Quick Keys Setup
          </h1>
          <p className="text-slate-500 mt-1">
            Configure 12 quick access buttons for your most sold products on the POS
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={clearAll}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="clear-all-quick-keys"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Clear All
          </Button>
          <Button
            onClick={saveQuickKeys}
            disabled={saving || !hasChanges}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-quick-keys"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Changes</>
            )}
          </Button>
        </div>
      </div>

      {/* Quick Keys Grid */}
      <Card className="border rounded-sm">
        <CardHeader className="border-b bg-slate-50">
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" /> Quick Key Tiles
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
            {quickKeys.map((qk, index) => (
              <div key={index} className="relative">
                {qk ? (
                  <button
                    onClick={() => setEditingSlot(index)}
                    className={`w-full aspect-square ${qk.color || QUICK_KEY_COLORS[index]} text-white rounded-lg p-2 flex flex-col items-center justify-center text-center shadow-md hover:shadow-lg transition-all hover:scale-105`}
                    data-testid={`quick-key-slot-${index}`}
                  >
                    <span className="text-xs font-bold leading-tight line-clamp-2">
                      {qk.product_name}
                    </span>
                    <span className="text-[10px] mt-1 opacity-80">
                      ${qk.sell_price?.toFixed(2)}
                    </span>
                    <span className="absolute top-1 left-1 text-[9px] bg-black/20 px-1 rounded">
                      #{index + 1}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => setEditingSlot(index)}
                    className="w-full aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center hover:border-emerald-400 hover:bg-emerald-50 transition-all group"
                    data-testid={`quick-key-empty-slot-${index}`}
                  >
                    <Plus className="w-6 h-6 text-slate-400 group-hover:text-emerald-500" />
                    <span className="text-xs text-slate-400 mt-1">Slot {index + 1}</span>
                  </button>
                )}
                {qk && (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeProduct(index); }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600"
                    data-testid={`remove-quick-key-${index}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Product Assignment Panel */}
      {editingSlot !== null && (
        <Card className="border rounded-sm border-emerald-200 bg-emerald-50/30">
          <CardHeader className="border-b bg-emerald-50">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                <Package className="w-5 h-5" /> Assign Product to Slot #{editingSlot + 1}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setEditingSlot(null); setSearchQuery(''); }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search products by name, SKU, or barcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                autoFocus
                data-testid="quick-key-product-search"
              />
            </div>

            {searchQuery.length >= 2 && (
              <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                {filteredProducts.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">
                    No products found matching "{searchQuery}"
                  </div>
                ) : (
                  filteredProducts.map(product => (
                    <button
                      key={product.id}
                      onClick={() => assignProduct(product)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                      data-testid={`assign-product-${product.id}`}
                    >
                      <div>
                        <p className="font-medium text-slate-900">{product.name}</p>
                        <p className="text-sm text-slate-500">
                          {product.sku && <span className="mr-3">SKU: {product.sku}</span>}
                          {product.barcode && <span>Barcode: {product.barcode}</span>}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">${product.sell_price?.toFixed(2)}</p>
                        <p className="text-xs text-slate-400">Stock: {product.quantity}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {searchQuery.length < 2 && (
              <p className="text-center text-slate-500 py-4">
                Type at least 2 characters to search products
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="border rounded-sm bg-slate-50">
        <CardContent className="p-4">
          <h3 className="font-bold text-slate-700 mb-2">How to use Quick Keys:</h3>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>1. Click on an empty slot or existing product to edit</li>
            <li>2. Search for a product by name, SKU, or barcode</li>
            <li>3. Click the product to assign it to the slot</li>
            <li>4. Click "Save Changes" to save your configuration</li>
            <li>5. Use Quick Keys on the POS screen for one-click sales</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickKeysSettings;
